import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { ModeProvider } from './contexts/ModeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ActivateInvite from './pages/ActivateInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptanceOverviewV2Page from './pages/AcceptanceOverviewV2Page';
import ConditionLifecyclePage from './pages/ConditionLifecyclePage';
import SpatialOperationsPage from './pages/SpatialOperationsPage';
import AcceptanceSiteView from './pages/AcceptanceSiteView';
import AcceptanceImpactOverview from './pages/AcceptanceImpactOverview';
import ScheduledScanDefinitionsPage from './pages/ScheduledScanDefinitionsPage';
import SafetyReportsDashboard from './pages/SafetyReportsDashboard';
import SafetyReportArtifactDetail from './pages/SafetyReportArtifactDetail';
import SignedSafetyControlRecordPage from './pages/SignedSafetyControlRecordPage';
import SignedRecordPreviewPage from './pages/dev/SignedRecordPreviewPage';
import SellableSiteExperiencePrototypePage from './pages/dev/SellableSiteExperiencePrototypePage';
import OverviewV2FixturePage from './pages/dev/OverviewV2FixturePage';
import SpatialOperationsFixturePage from './pages/dev/SpatialOperationsFixturePage';
import ShowcasePage from './pages/ShowcasePage';
import AskEdgentikPage from './pages/AskEdgentikPage';
import ShowcaseBackupRoute from './pages/ShowcaseBackupRoute';
import CameraMeasurementFixturePage from './pages/dev/CameraMeasurementFixturePage';
import Ux2GoldensFixturePage from './pages/dev/Ux2GoldensFixturePage';
import Sr2GoldensFixturePage from './pages/dev/Sr2GoldensFixturePage';
import { buildSiteConditionOverviewRedirectPath } from './domain/sellableSiteExperience/shellContext';
import AcceptanceDemoStoryReport from './pages/AcceptanceDemoStoryReport';
import AcceptanceResolutionWalkthrough from './pages/AcceptanceResolutionWalkthrough';
import AcceptanceProofWorkspace from './pages/AcceptanceProofWorkspace';
import AcceptanceScanVerifyPage from './pages/AcceptanceScanVerifyPage';
import AcceptanceSafetyCasesHome from './pages/AcceptanceSafetyCasesHome';
import SafetyCaseDetailPage from './pages/SafetyCaseDetailPage';
import AcceptanceProofTechnicalDetails from './pages/AcceptanceProofTechnicalDetails';
import SetupRolloutHome from './pages/SetupRolloutHome';
import LiveOpsHome from './pages/LiveOpsHome';
import LiveMonitor from './pages/LiveMonitor';
import LiveView from './pages/LiveView';
import Analytics from './pages/Analytics';
import MeasurePage from './pages/MeasurePage';
import Incidents from './pages/Incidents';
import Zones from './pages/Zones';
import GrafanaDashboards from './pages/GrafanaDashboards';
import OdooERP from './pages/OdooERP';
import Configurations from './pages/Configurations';
import Devices from './pages/Devices';
import DeviceControl from './pages/DeviceControl';
import DeviceControlLauncher from './pages/DeviceControlLauncher';
import Deployments from './pages/Deployments';
import Agents from './pages/Agents';
import UserAdmin from './pages/UserAdmin';
import AccessScopeAdmin from './pages/AccessScopeAdmin';
import SetupWizard from './pages/SetupWizard';
import Preflight from './pages/Preflight';
import BringUp from './pages/BringUp';
import OpsCopilotRunbooks from './pages/ops/OpsCopilotRunbooks';
import EmbeddedCopilot from './pages/ops/EmbeddedCopilot';
import DisasterRecoveryContinuity from './pages/ops/DisasterRecoveryContinuity';
import Cameras from './pages/Cameras';
import Policies from './pages/Policies';
import Blueprints from './pages/Blueprints';
import ConfigSnapshots from './pages/ConfigSnapshots';
import CameraWorkspace from './pages/CameraWorkspace';
import Diagnostics from './pages/Diagnostics';
import AuditLogPage from './pages/AuditLog';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import ExternalSafetyReviewLayout from './components/externalReview/ExternalSafetyReviewLayout';
import ExternalSafetyReviewRouteGate from './components/externalReview/ExternalSafetyReviewRouteGate';
import ExternalSafetyReviewPage from './pages/ExternalSafetyReviewPage';
import ExternalSafetyReviewHome from './pages/ExternalSafetyReviewHome';
import HomeRouter from './components/HomeRouter';
import RouteRenderBoundary from './components/RouteRenderBoundary';

/** Compatibility redirect: Site condition bookmarks fold into Overview. */
function SiteConditionToOverviewRedirect() {
  const location = useLocation();
  return <Navigate to={buildSiteConditionOverviewRedirectPath(location.search)} replace />;
}

function ShowcaseRouteBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <RouteRenderBoundary resetKey={`${location.pathname}${location.search || ''}`}>
      {children}
    </RouteRenderBoundary>
  );
}

// Custom theme for warehouse safety
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1565c0',
      light: '#5e92f3',
      dark: '#003c8f',
    },
    secondary: {
      main: '#ff6f00',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
    },
    success: {
      main: '#2e7d32',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/activate" element={<ActivateInvite />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dev/ux2-goldens" element={<Ux2GoldensFixturePage />} />
            <Route path="/dev/sr2-goldens" element={<Sr2GoldensFixturePage />} />
            <Route path="/dev/spatial-operations-goldens" element={<SpatialOperationsFixturePage />} />
            <Route
              path="/showcase"
              element={
                <ShowcaseRouteBoundary>
                  <ProtectedRoute>
                    <ShowcasePage />
                  </ProtectedRoute>
                </ShowcaseRouteBoundary>
              }
            />
            <Route
              path="/showcase2"
              element={
                <ShowcaseRouteBoundary>
                  <ProtectedRoute>
                    <ShowcaseBackupRoute />
                  </ProtectedRoute>
                </ShowcaseRouteBoundary>
              }
            />
            {/* Dev-only fixture harnesses that intentionally remain outside the app shell. */}
            {process.env.NODE_ENV === 'development' ? (
              <>
                <Route
                  path="/dev/signed-record-preview"
                  element={<SignedRecordPreviewPage />}
                />
                <Route
                  path="/dev/sellable-site-experience"
                  element={<SellableSiteExperiencePrototypePage />}
                />
                <Route
                  path="/dev/spatial-operations"
                  element={<SpatialOperationsFixturePage />}
                />
              </>
            ) : null}
            <Route
              path="/ask-edgentik"
              element={
                <ProtectedRoute>
                  <AskEdgentikPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/review"
              element={
                <ProtectedRoute>
                  <ExternalSafetyReviewRouteGate mode="home">
                    <ExternalSafetyReviewLayout>
                      <ExternalSafetyReviewHome />
                    </ExternalSafetyReviewLayout>
                  </ExternalSafetyReviewRouteGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/review/safety-scans/:reportId"
              element={
                <ProtectedRoute>
                  <RoleProtectedRoute anyCapability={['safety_case:view_selected', 'safety_case:view', 'signed_scan:view_selected', 'signed_scan:view']}>
                    <ExternalSafetyReviewRouteGate mode="report">
                      <ExternalSafetyReviewLayout>
                        <ExternalSafetyReviewPage />
                      </ExternalSafetyReviewLayout>
                    </ExternalSafetyReviewRouteGate>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  {/* ModeProvider performs background probes (devices, ui/config, edge health).
                      Keep it inside ProtectedRoute to avoid request/redirect loops on /login. */}
                  <ModeProvider>
                    <Layout />
                  </ModeProvider>
                </ProtectedRoute>
              }
            >
              {/* Safety Monitoring Routes */}
              <Route index element={<HomeRouter />} />
              <Route
                path="overview"
                element={
                  <RoleProtectedRoute access="overview">
                    <Navigate to="/acceptance/overview" replace />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/overview"
                element={
                  <RoleProtectedRoute access="overview">
                    <AcceptanceOverviewV2Page />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/overview-v2"
                element={
                  <RoleProtectedRoute access="overview">
                    <AcceptanceOverviewV2Page />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/lifecycle"
                element={
                  <RoleProtectedRoute access="overview">
                    <ConditionLifecyclePage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="spatial-operations"
                element={
                  <RoleProtectedRoute access="overview">
                    <SpatialOperationsPage />
                  </RoleProtectedRoute>
                }
              />
              {process.env.NODE_ENV === 'development' ? (
                <>
                <Route
                  path="dev/acceptance/overview-v2"
                  element={
                    <RoleProtectedRoute access="overview">
                      <OverviewV2FixturePage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="dev/camera-measurement"
                  element={
                    <RoleProtectedRoute access="overview">
                      <CameraMeasurementFixturePage />
                    </RoleProtectedRoute>
                  }
                />
                </>
              ) : null}
              <Route
                path="acceptance/site-view"
                element={
                  <RoleProtectedRoute access="overview">
                    <AcceptanceSiteView />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/site-condition"
                element={
                  <RoleProtectedRoute access="overview">
                    <SiteConditionToOverviewRedirect />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/impact"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceImpactOverview />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/impact/walkthrough/:scenarioId"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceResolutionWalkthrough />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="setup-rollout"
                element={
                  <RoleProtectedRoute access="setupRollout">
                    <SetupRolloutHome />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance"
                element={<Navigate to="/acceptance/reports" replace />}
              />
              <Route
                path="acceptance/cases"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceSafetyCasesHome />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/cases/:caseId"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <SafetyCaseDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/scan-definitions"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <ScheduledScanDefinitionsPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/reports"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <SafetyReportsDashboard />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/reports/artifacts/:reportId"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <SafetyReportArtifactDetail />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/reports/records/:recordId/revisions/:revision"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <SignedSafetyControlRecordPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/reports/records/:recordId"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <SignedSafetyControlRecordPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/proof"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceProofWorkspace />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/scans/:reportId/verify"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceScanVerifyPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="prove"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceProofWorkspace />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="overview/demo-story"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <Navigate to="/acceptance/impact/walkthrough/pedestrian-forklift" replace />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/case-studies/:caseId"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceDemoStoryReport />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="acceptance/proof/technical"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceProofTechnicalDetails />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="prove/technical"
                element={
                  <RoleProtectedRoute access="acceptance">
                    <AcceptanceProofTechnicalDetails />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="live-ops"
                element={
                  <RoleProtectedRoute access="liveOps">
                    <LiveOpsHome />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="live-view"
                element={
                  <RoleProtectedRoute access="liveView">
                    <LiveView />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="live"
                element={
                  <RoleProtectedRoute access="liveAlerts">
                    <LiveMonitor />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="analytics"
                element={
                  <RoleProtectedRoute access="analytics">
                    <Analytics />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="accuracy"
                element={
                  <RoleProtectedRoute access="accuracy">
                    <MeasurePage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="incidents"
                element={
                  <RoleProtectedRoute access="incidents">
                    <Incidents />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="zones"
                element={
                  <RoleProtectedRoute access="zones">
                    <Zones />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="grafana"
                element={
                  <RoleProtectedRoute access="grafana">
                    <GrafanaDashboards />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="odoo"
                element={
                  <RoleProtectedRoute access="odoo">
                    <OdooERP />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="audit"
                element={
                  <RoleProtectedRoute access="audit">
                    <AuditLogPage />
                  </RoleProtectedRoute>
                }
              />
              <Route path="ocr/*" element={<HomeRouter />} />
              
              {/* Configuration Routes */}
              <Route
                path="users"
                element={
                  <RoleProtectedRoute access="users">
                    <UserAdmin />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="admin/access-scope"
                element={
                  <RoleProtectedRoute access="accessScope">
                    <AccessScopeAdmin />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="configurations"
                element={
                  <RoleProtectedRoute access="configurations">
                    <Configurations />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="devices"
                element={
                  <RoleProtectedRoute access="devices">
                    <Devices />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="devices/control"
                element={
                  <RoleProtectedRoute access="deviceControl">
                    <DeviceControlLauncher />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="devices/:deviceId/control"
                element={
                  <RoleProtectedRoute access="deviceControl">
                    <DeviceControl />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="deployments"
                element={
                  <RoleProtectedRoute access="deployments">
                    <Deployments />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="agents"
                element={
                  <RoleProtectedRoute access="agents">
                    <Agents />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="config-snapshots"
                element={
                  <RoleProtectedRoute access="configSnapshots">
                    <ConfigSnapshots />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="cameras"
                element={
                  <RoleProtectedRoute access="cameras">
                    <Cameras />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="cameras/:cameraId"
                element={
                  <RoleProtectedRoute access="cameras">
                    <CameraWorkspace />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="policies"
                element={
                  <RoleProtectedRoute access="policies">
                    <Policies />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="blueprints"
                element={
                  <RoleProtectedRoute access="blueprints">
                    <Blueprints />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="bringup"
                element={
                  <RoleProtectedRoute access="bringup">
                    <BringUp />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="ops/runbooks"
                element={
                  <RoleProtectedRoute access="opsRunbooks">
                    <OpsCopilotRunbooks />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="ops/copilot"
                element={
                  <RoleProtectedRoute access="opsCopilot">
                    <EmbeddedCopilot />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="ops/dr"
                element={
                  <RoleProtectedRoute access="opsDr">
                    <DisasterRecoveryContinuity />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="setup"
                element={
                  <RoleProtectedRoute access="setupWizard">
                    <SetupWizard />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="preflight"
                element={
                  <RoleProtectedRoute access="preflight">
                    <Preflight />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="diagnostics"
                element={
                  <RoleProtectedRoute access="diagnostics">
                    <Diagnostics />
                  </RoleProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </Box>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
